import type {KeyboardEvent, ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {Text, TextSize, TextType} from '@Components/text';
import {convertSecondsToMMSSFormat, numberToMMSSFormat} from '@Utils/date.util';
import {openEditPosterSubtitleMoreOptionsModal} from '@Modals/edit-poster-subtitle-more-options-modal';
import {noop} from '@Utils/general.util';
import {ClickableDiv} from '@Components/clickable-div';
import {SubtitleBoxTextArea} from '@Components/poster-editor/components/poster-editor-subtitles-editor/components/subtitles-editor/subtitle-editing-box/components/subtitle-box-text-area/subtitle-box-text-area';
import type {SubtitleEditngBoxProps} from './subtitle-editing-box.types';
import {SubtitlesEditorKeyDown} from './subtitle-editing-box.types';
import styles from './subtitle-editing-box.module.scss';
import {useAppSelector} from '@/hooks';
import {getIsCurrentPagePlaying} from '@Components/poster-editor/poster-editor-reducer';

export function SubtitleEditingBox({
  isSelected = false,
  disableAddAfter = false,
  disableMergeWithNextOption = false,
  disableMergeWithPreviousOption = false,
  onSubtitleDelete = noop,
  onAddSubtitleAfter = noop,
  onSubtitleBoxFocussed = noop,
  onSubtitleTextChange = noop,
  onMergeSubtitles = noop,
  onKeyDown = noop,
  setAutoFocus = noop,
  onPlayPauseSubtitle = noop,
  isMobileVariant = false,
  autoFocus = false,
  ...props
}: SubtitleEditngBoxProps): ReactElement<any> {
  const [numOfRows, setNumOfRows] = useState(1);
  const subtitleBoxRef = useRef<HTMLDivElement>(null);
  const inputFieldTextAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const [caretPositionBeforeBlur, setCaretPositionBeforeBlur] = useState(0);

  const [isEditingText, setIsEditingText] = useState(false);

  const [textDisplayValue, setDisplayTextValue] = useState(props.text);

  const isPlaying = useAppSelector(getIsCurrentPagePlaying);

  useEffect(() => {
    setDisplayTextValue(props.text);
  }, [props.text]);

  useEffect(() => {
    if (isMobileVariant) {
      return;
    }

    if (isSelected && autoFocus) {
      subtitleBoxRef.current?.focus();
      setAutoFocus(false);
    }
  }, [isSelected, autoFocus]);

  const handleSubtitleBoxOnBlur = (): void => {
    if (!inputFieldTextAreaRef.current) {
      return;
    }

    inputFieldTextAreaRef.current.blur();
  };

  const handleSubtitleBoxOnFocus = (): void => {
    if (!inputFieldTextAreaRef.current) {
      return;
    }

    onSubtitleBoxFocussed(props.subtitleId);

    if (!isMobileVariant) {
      inputFieldTextAreaRef.current.focus();
    }
  };

  const handleSubtitleTextChange = (value: string): void => {
    setDisplayTextValue(value);
    setIsEditingText(true);
    onSubtitleTextChange(props.subtitleId, value, false);
  };

  const handleSubtitleTextChangeEnded = (value: string): void => {
    onSubtitleTextChange(props.subtitleId, value);
    setIsEditingText(false);
  };

  const getCaretPosition = (): number | undefined => {
    const inputFieldTextAreaElement = document.getElementsByClassName(`js-input-field-${props.subtitleId}`);
    if (!inputFieldTextAreaElement[0]) {
      return undefined;
    }

    return (inputFieldTextAreaElement[0] as HTMLTextAreaElement).selectionStart;
  };

  const handleBackSpace = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    const caretPosition = getCaretPosition();

    if (inputFieldTextAreaRef.current?.selectionStart !== inputFieldTextAreaRef.current?.selectionEnd) {
      return;
    }

    if (caretPosition === 0) {
      e.preventDefault();
      onKeyDown(props.subtitleId, SubtitlesEditorKeyDown.BACKSPACE);
    }
  };

  const handleArrowDown = (): void => {
    const subtitleTextLength = textDisplayValue.length;

    const caretPosition = getCaretPosition();

    if (caretPosition === subtitleTextLength) {
      onKeyDown(props.subtitleId, SubtitlesEditorKeyDown.DOWN);
    }
  };

  const handleArrowUp = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    const caretPosition = getCaretPosition();

    if (caretPosition === 0) {
      onKeyDown(props.subtitleId, SubtitlesEditorKeyDown.UP);
      e.preventDefault();
    }
  };

  const onSplitSubtitle = (): void => {
    if (!inputFieldTextAreaRef.current) {
      return;
    }

    const caretPosition = getCaretPosition() || caretPositionBeforeBlur;

    onKeyDown(props.subtitleId, SubtitlesEditorKeyDown.ENTER, caretPosition);
  };

  const getPlayPauseIcon = (): ReactElement<any> => {
    return (
      <Icon
        onClick={() => {
          onPlayPauseSubtitle(props.subtitleId);
        }}
        icon={isPlaying ? 'icon-pause' : 'icon-play-video'}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.SQUARE}
        type={IconType.GHOST_UNCOLORED}
        className="spacing-m-r-2"
      />
    );
  };

  const getSubtitleEditingBoxHeaderRow = (): ReactElement<any> => {
    return (
      <div className="flex-row-justify-between flex-items-center _full-width spacing-m-b-2">
        <div className="flex-center">
          {isSelected ? getPlayPauseIcon() : null}
          <Text val={`${convertSecondsToMMSSFormat(props.startTime)} - ${convertSecondsToMMSSFormat(props.endTime)}`} size={TextSize.XXSMALL} type={TextType.BODY} />
        </div>
        {isSelected ? (
          <div>
            <Icon onClick={onSplitSubtitle} icon="icon-cut" size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} type={IconType.GHOST_UNCOLORED} className="spacing-m-r-2" />
            <Icon
              className="color-danger-100"
              onClick={() => {
                onSubtitleDelete(props.subtitleId);
              }}
              icon="icon-delete"
              size={IconSize.SIZE_ICON_16}
              shape={IconShape.SQUARE}
              type={IconType.GHOST_UNCOLORED}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const getSubtitleEditingBoxHeaderRowForMobile = (): ReactElement<any> => {
    return (
      <div className="flex-row-justify-between flex-items-center _full-width spacing-m-b-2">
        <div className="flex-center">
          <Text val={`${numberToMMSSFormat(props.startTime)} - ${numberToMMSSFormat(props.endTime)}`} size={TextSize.XXSMALL} type={TextType.BODY} />
        </div>

        <div>
          {isSelected ? (
            <Icon onClick={onSplitSubtitle} icon="icon-cut" size={IconSize.SIZE_ICON_16} shape={IconShape.SQUARE} type={IconType.GHOST_UNCOLORED} className="spacing-m-r-2" />
          ) : null}
          <Icon
            onClick={() => {
              openEditPosterSubtitleMoreOptionsModal({
                subtitleId: props.subtitleId,
                disableAddAfter,
                disableMergeWithNextOption,
                disableMergeWithPreviousOption,
                onMergeSubtitles,
                onSubtitleDelete,
                addSubtitleAfter: onAddSubtitleAfter,
              });
            }}
            icon="icon-menu-dots"
            size={IconSize.SIZE_ICON_16}
            shape={IconShape.SQUARE}
            type={IconType.GHOST_UNCOLORED}
          />
        </div>
      </div>
    );
  };

  const handleDynamicHeightForInputFieldTextArea = (): void => {
    if (textDisplayValue === '') {
      return;
    }

    const hiddenTextComponent = document.querySelector(`.js-hidden-text-component-${props.subtitleId}`);
    if (!hiddenTextComponent) {
      return;
    }

    const hiddenTextComponentHeight = hiddenTextComponent.clientHeight;
    const heightToSetForInputFieldTextArea = hiddenTextComponentHeight + 10;

    const inputFieldTextArea = document.querySelector(`.js-input-field-${props.subtitleId}`);
    if (!inputFieldTextArea) {
      return;
    }

    (inputFieldTextArea as HTMLTextAreaElement).style.height = `${heightToSetForInputFieldTextArea}px`;
  };

  const getLineHeightValueForHiddenTextComponent = (hiddenTextComponent: Element): number => {
    const computedStyle = window.getComputedStyle(hiddenTextComponent);
    const lineHeightStr = computedStyle.lineHeight;

    if (lineHeightStr.endsWith('px')) {
      return parseInt(lineHeightStr, 10);
    }
    return parseFloat(lineHeightStr) * parseFloat(computedStyle.fontSize);
  };

  const setMaxNumOfRows = (): void => {
    if (textDisplayValue === '') {
      setNumOfRows(1);
      return;
    }

    const hiddenTextComponent = document.querySelector(`.js-hidden-text-component-${props.subtitleId}`);
    if (!hiddenTextComponent) {
      return;
    }

    const hiddenTextComponentHeight = hiddenTextComponent.clientHeight;
    const lineHeight = getLineHeightValueForHiddenTextComponent(hiddenTextComponent);
    const numOfRowsForSubtitleText = hiddenTextComponentHeight / lineHeight;
    setNumOfRows(numOfRowsForSubtitleText);
  };

  useEffect(() => {
    handleDynamicHeightForInputFieldTextArea();
    setMaxNumOfRows();
  }, [textDisplayValue, props.subtitleId]);

  const getSubtitleEditingBox = (): ReactElement<any> => {
    return (
      <ClickableDiv
        ref={subtitleBoxRef}
        onClick={handleSubtitleBoxOnFocus}
        onBlur={handleSubtitleBoxOnBlur}
        onFocus={handleSubtitleBoxOnFocus}
        className={`${isSelected ? styles.selectedSubtitleOutline : ''} _full-width spacing-p-2 radius-8 ${styles.subtitleEditingBox}`}
      >
        {isMobileVariant ? getSubtitleEditingBoxHeaderRowForMobile() : getSubtitleEditingBoxHeaderRow()}
        <div className={styles.subtitleTextArea}>
          <Text
            className={`${styles.hiddenTextComponent} _invisible js-hidden-text-component-${props.subtitleId}`}
            val={textDisplayValue}
            size={TextSize.XSMALL}
            type={TextType.BODY}
          />
          <SubtitleBoxTextArea
            onInputBlur={() => {
              const caretPosition = getCaretPosition();
              if (!caretPosition) {
                return;
              }
              setCaretPositionBeforeBlur(caretPosition);
            }}
            onInputFocus={() => {
              if (!inputFieldTextAreaRef.current || caretPositionBeforeBlur) {
                return;
              }
              inputFieldTextAreaRef.current.setSelectionRange(textDisplayValue.length, textDisplayValue.length);
            }}
            ref={inputFieldTextAreaRef}
            allowOnChangeWithKeys
            onBackSpaceKeyDown={handleBackSpace}
            onArrowDownKeyDown={handleArrowDown}
            onArrowUpKeyDown={handleArrowUp}
            onInputSubmit={onSplitSubtitle}
            doBlurOnSubmit
            onInputChange={handleSubtitleTextChange}
            onInputChangeEnded={handleSubtitleTextChangeEnded}
            className="_no-border"
            maxNumberOfRows={numOfRows}
            input={textDisplayValue}
            inputClassName={`js-input-field-${props.subtitleId} ${styles.inputField}`}
          />
        </div>
      </ClickableDiv>
    );
  };

  return getSubtitleEditingBox();
}
